import '@/styles/globals.css'
import 'lenis/dist/lenis.css'

import type { FC, ReactNode } from 'react'
import type { AppProps } from 'next/app'
import type { NextPage } from 'next'
import type { Scene } from '@/types'

import Head from 'next/head'
import { useRef } from 'react'
import { useRouter } from 'next/router'
import { Mona_Sans, Instrument_Serif, Martian_Mono } from 'next/font/google'
import SmoothScroll from '@/components/motion/smooth-scroll'
import PageTransition from '@/components/motion/page-transition'
import SceneController from '@/components/motion/scene-controller'
import Cursor from '@/components/motion/cursor'
import Header from '@/components/generals/header'
import Footer from '@/components/generals/footer'
import Preloader from '@/components/generals/preloader'

// Mona Sans carries the site; its width axis powers the typographic motion.
const sans = Mona_Sans({
    subsets: ['latin', 'latin-ext'],
    axes: ['wdth'],
    display: 'swap'
})

// Instrument Serif italic for the editorial accent words.
const serif = Instrument_Serif({
    subsets: ['latin', 'latin-ext'],
    weight: '400',
    style: ['normal', 'italic'],
    display: 'swap'
})

// Martian Mono for labels, indices and measurements.
const mono = Martian_Mono({
    subsets: ['latin', 'latin-ext'],
    axes: ['wdth'],
    display: 'swap'
})

const fontVariables = `:root{--font-app-sans:${sans.style.fontFamily};--font-app-mono:${mono.style.fontFamily};--font-app-heading:${sans.style.fontFamily};--font-app-serif:${serif.style.fontFamily}}`

// A page can declare the environment it opens in: `Page.scene = 'ink'`.
type PageWithScene = NextPage & { scene?: Scene }

const App: FC<AppProps> = ({ Component, pageProps }): ReactNode => {
    const router = useRouter()
    const rootRef = useRef<HTMLDivElement>(null)
    const scene = (Component as PageWithScene).scene ?? 'paper'

    return (
        <SmoothScroll>
            <PageTransition>
                <div ref={rootRef} data-theme={scene} className='theme-root font-sans'>
                    <Head>
                        <meta name='viewport' content='width=device-width, initial-scale=1' />
                        <style dangerouslySetInnerHTML={{ __html: fontVariables }} />
                    </Head>

                    <Preloader />
                    <Header />
                    <Component {...pageProps} />
                    <Footer />
                    <Cursor />
                    <SceneController rootRef={rootRef} initial={scene} routeKey={router.asPath} />
                </div>
            </PageTransition>
        </SmoothScroll>
    )
}

export default App
